// SeaFrameLayer — renders a SeaFrame around a board (docs/sea-design.md).
// Everything is derived from `tiles` + `mode` and keyed on the terrain
// signature (not the per-publish tiles array), so the geometry and the traffic
// plan are built once per board, never per snapshot.
//
//   open        SeaRing (one ocean-ring mesh) + AmbientSea (all-ring loops)
//   rim         AmbientSea only (short coastal loops in the existing band)
//   landlocked  WallMist (curtain + wisps); no sea, so no traffic
//
// For `landlocked` the CALLER feeds in tiles already run through
// applyLandlockedWall (the wall is real terrain through the normal pipeline).

import type { SceneNode } from '@aod/protocol';
import { useMemo, useRef } from 'react';
import { buildBiomeMap, waterSurfaceY } from '../elevation.js';
import { shoreBoardFor } from '../shore/shoreBoard.js';
import { AmbientSea } from './AmbientSea.js';
import { SeaRing } from './SeaRing.js';
import { WallMist } from './WallMist.js';
import { RIM_FRAME, type SeaFrame, type SeaMode, extraOceanRings } from './seaFrame.js';
import { ringSurfaceY } from './seaGeometry.js';
import { EMPTY_PLAN, planTraffic } from './seaRoutes.js';
import {
  type BoundaryEdge,
  boardBiomes,
  boundaryEdges,
  key,
  outwardRings,
  seedOf,
  terrainSignature,
  wallRows,
  worldToAxial,
} from './seaTiles.js';

/** Lift of a hull above the sampled water surface. */
const SHIP_LIFT = 0.03;

function distToBoundary(x: number, z: number, edges: ReadonlyArray<BoundaryEdge>): number {
  let d = Number.POSITIVE_INFINITY;
  for (const e of edges) {
    const tx = -e.nz;
    const tz = e.nx;
    const t = Math.max(-0.5, Math.min(0.5, (x - e.x) * tx + (z - e.z) * tz));
    d = Math.min(d, Math.hypot(x - (e.x + tx * t), z - (e.z + tz * t)));
  }
  return d;
}

export function SeaFrameLayer({
  tiles,
  mode,
  frame,
  ships = true,
}: {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly mode: SeaMode;
  /** The frame the board was generated with (the sim publishes it). */
  readonly frame: SeaFrame;
  /** Debug switch: false hides ships + gulls. */
  readonly ships?: boolean;
}) {
  const { oceanRings, wallRings } = frame;
  const signature = useMemo(() => terrainSignature(tiles), [tiles]);
  const latest = useRef(tiles);
  latest.current = tiles;

  const derived = useMemo(() => {
    const cur = latest.current;
    const board = boardBiomes(cur);
    const boardSet = new Set(board.keys());
    const extra = mode === 'open' ? Math.max(0, oceanRings - RIM_FRAME.oceanRings) : 0;
    const edges = boundaryEdges(boardSet);
    const ring = outwardRings(boardSet, extra);
    const wall = mode === 'landlocked' ? wallRows(cur, wallRings) : new Map<string, number>();
    const biomeMap = buildBiomeMap(cur);
    const waterY = (x: number, z: number) => {
      const [q, r] = worldToAxial(x, z);
      const b = board.get(key(q, r));
      const y =
        b !== undefined ? waterSurfaceY(q, r, biomeMap) : ringSurfaceY(distToBoundary(x, z, edges));
      return y + SHIP_LIFT;
    };
    const seed = seedOf(signature);
    const plan = ships ? planTraffic({ mode, seed, board, extraRings: extra, waterY }) : EMPTY_PLAN;
    // The open-sea ring takes its colour from the board's shore field, so the board's water and the ring are one
    // continuous sea (no seam at the board edge).
    const landDist = shoreBoardFor(cur).field.distAt;
    return { ring, edges, wall, plan, seed, landDist };
  }, [signature, mode, oceanRings, wallRings, ships]);

  return (
    <group name="sea-frame">
      {mode === 'open' && (
        <SeaRing
          tiles={derived.ring}
          boundary={derived.edges}
          landDist={derived.landDist}
          signature={`${signature}:${oceanRings}`}
        />
      )}
      {mode === 'landlocked' && (
        <WallMist
          edges={derived.edges}
          wall={derived.wall}
          signature={signature}
          seed={derived.seed}
        />
      )}
      <AmbientSea plan={derived.plan} />
    </group>
  );
}
